/**
 * packages/contracts/src/schemas.ts
 * -------------------------------------------------------
 * Purpose:
 *   Central place for all zod schemas and inferred TypeScript
 *   types that are shared between the API and the Web app.
 *
 * High-level data flow:
 *   - API uses these schemas in the ts-rest contract to validate
 *     request/response shapes.
 *   - Web imports the same inferred types to keep UI code aligned
 *     with backend responses.
 */
import { z } from 'zod';
import { ReviewActionSchema, ReviewDecisionSchema, ReviewHistorySchema } from './review-decisions.js';
/** Standard API error payload used by every endpoint. */
export const ApiErrorSchema = z.object({
    /** Stable, machine-readable error code. */
    code: z.string(),
    /** Human-readable error message safe to show to users. */
    message: z.string(),
    /** Optional structured details for debugging (never rely on this in UI). */
    details: z.unknown().optional(),
});
/** RBAC role for an org member. */
export const OrgRoleSchema = z.enum(['admin', 'member']);
/** Organization record returned to the client. */
export const OrgSchema = z.object({
    /** Organization ID (uuid). */
    id: z.string().uuid(),
    /** Human-friendly org name. */
    name: z.string().min(1),
});
/** Optional UI branding returned for the active org. */
export const OrgBrandingSchema = z.object({
    /** Optional header logo URL. */
    logoUrl: z.string().min(1).optional(),
    /** Optional accessible alt text for the header logo. */
    logoAltText: z.string().min(1).optional(),
});
/** Active org payload returned by auth-protected endpoints like /api/me. */
export const ActiveOrgSchema = OrgSchema.extend({
    /** Current org branding for the signed-in experience. */
    branding: OrgBrandingSchema,
});
/** User record exposed to the client (kept intentionally minimal). */
export const UserSchema = z.object({
    /** User ID (uuid). */
    id: z.string().uuid(),
    /** Email address used for login. */
    email: z.string().email(),
    /** Display name. */
    name: z.string().nullable().optional(),
});
/** Audit status values (mirrors the original prototype). */
export const AuditStatusSchema = z.enum(['Ready', 'Review', 'Do Not Order']);
/** Local manual-review status values stored in the app DB. */
export const AuditReviewStatusSchema = z.enum(['Resolved']);
/** Filter values supported by inbox-style resolved filtering. */
export const AuditResolvedFilterSchema = z.enum(['all', 'resolved', 'unresolved']);
/** Shared date range buckets used by analytics-style pages. */
export const AnalyticsRangeSchema = z.enum(['today', 'week-to-date', 'last-7-days']);
/** Stored answers for the current local audit review workflow. */
export const AuditReviewAnswersSchema = z.object({
    /** Whether the audit caught an issue that could have caused a jobsite alert. */
    caughtJobsiteAlertIssue: z.boolean().nullable(),
    /** Whether the audit report helped the reviewer become aware of a potential installation issue. */
    awareOfPotentialInstallationIssue: z.boolean().nullable(),
    /** Whether corrective action was taken on the job. */
    tookCorrectiveAction: z.boolean().nullable(),
});
/** Optional reviewer explanations captured when a question is answered "No". */
export const AuditReviewExplanationsSchema = z.object({
    /** Optional explanation for the jobsite-alert-risk question. */
    caughtJobsiteAlertIssue: z.string().nullable(),
    /** Optional explanation for the installation-awareness question. */
    awareOfPotentialInstallationIssue: z.string().nullable(),
    /** Optional explanation for the corrective-action question. */
    tookCorrectiveAction: z.string().nullable(),
});
/** Local manual-review state persisted for an audit in the app DB. */
export const AuditReviewStateSchema = z.object({
    sourceVersion: z.string().nullable().default(null),
    revision: z.string().nullable().default(null),
    decision: ReviewDecisionSchema.nullable().default(null),
    history: z.array(ReviewHistorySchema).default([]),
    /** Current manual-review status. */
    status: AuditReviewStatusSchema.nullable(),
    /** Stored answers for the current workflow. */
    answers: AuditReviewAnswersSchema,
    /** Optional reviewer explanations for "No" answers. */
    explanations: AuditReviewExplanationsSchema,
    /** Last update timestamp from the local app DB. */
    updatedAt: z.string().datetime().nullable(),
});
/** Labeling status for human review workflows. */
export const LabelStatusSchema = z.enum(['Unlabeled', 'Labeled', 'Unknown']);
/** Ground truth status used for model evaluation. */
export const GroundTruthSchema = z.enum(['Yes', 'No', 'Unknown']);
/** Issue severity levels. */
export const IssueSeveritySchema = z.enum(['Critical', 'Caution']);
/** A single issue flagged on an audit. */
export const IssueSchema = z.object({
    /** Human-friendly issue type. */
    type: z.string().min(1),
    /** Severity used for sorting / prioritization. */
    severity: IssueSeveritySchema,
});
/** Attachment metadata exposed for submitted form fields. */
export const SubmittedFormAttachmentSchema = z.object({
    /** Stored ServiceTitan attachment path when available. */
    fileName: z.string().optional(),
    /** Original upload filename when available. */
    originalFileName: z.string().optional(),
});
/** Small key/value item shown in the compact submitted-form overview strip. */
export const SubmittedFormOverviewItemSchema = z.object({
    /** Human-friendly overview label. */
    label: z.string().min(1),
    /** Pre-normalized overview value safe to render directly. */
    value: z.string().min(1),
});
/** A single normalized field inside a submitted form section. */
export const SubmittedFormFieldSchema = z.object({
    /** Stable field id from the source payload (or a server fallback). */
    id: z.string().min(1),
    /** Human-friendly field label shown in the UI. */
    label: z.string().min(1),
    /** Source field type used by the frontend renderer. */
    type: z.string().min(1),
    /** Scalar field value normalized to a display-safe string when present. */
    value: z.string().nullable().optional(),
    /** Checkbox or multi-select values when present. */
    values: z.array(z.string()).optional(),
    /** Attachment metadata for picture or file-backed fields. */
    attachments: z.array(SubmittedFormAttachmentSchema),
});
/** A normalized section within a submitted form. */
export const SubmittedFormSectionSchema = z.object({
    /** Stable section id from the source payload (or a server fallback). */
    id: z.string().min(1),
    /** Human-friendly section title shown in the accordion. */
    title: z.string().min(1),
    /** Ordered list of normalized fields in this section. */
    fields: z.array(SubmittedFormFieldSchema),
});
/** A normalized submitted form ready for the dedicated form viewer. */
export const SubmittedFormSchema = z.object({
    /** Stable submitted-form id from the source payload (or a server fallback). */
    id: z.string().min(1),
    /** Source form name displayed above the overview and accordion. */
    formName: z.string().min(1),
    /** Optional source status (for example, Completed). */
    status: z.string().optional(),
    /** Optional submission timestamp for the form itself. */
    submittedAt: z.string().datetime().optional(),
    /** Compact overview items surfaced above the accordion sections. */
    overview: z.array(SubmittedFormOverviewItemSchema),
    /** Ordered list of normalized sections for the read-only viewer. */
    sections: z.array(SubmittedFormSectionSchema),
});
/** A minimal estimate shape used by the dedicated estimate cards. */
export const AuditEstimateSchema = z.object({
    /** Stable estimate id normalized to a string for display fallbacks. */
    id: z.string().min(1),
    /** Optional estimate name from ServiceTitan. */
    name: z.string().optional(),
    /** Human-friendly estimate status. */
    status: z.string().min(1),
    /** Unformatted subtotal used by the UI currency formatter. */
    subtotal: z.number(),
});
/** A minimal audit shape used for lists. */
export const AuditSummarySchema = z.object({
    /** Audit ID (ServiceTitan job id as string; unique within a tenant). */
    id: z.string().min(1),
    /** Job identifier shown to ops users (e.g. "#308618834"). */
    jobId: z.string().min(1),
    /** Technician name (denormalized for simplicity). */
    technicianName: z.string().min(1),
    /** City/region for quick filtering. */
    location: z.string().min(1),
    /** When the audit was submitted (ISO string). */
    submittedAt: z.string().datetime(),
    /** Current audit status. */
    status: AuditStatusSchema,
    /** Local manual-review state stored in the app DB. */
    reviewState: AuditReviewStateSchema,
    /** Current labeling status. */
    labelStatus: LabelStatusSchema,
    /** Optional ground truth status if labeled. */
    groundTruth: GroundTruthSchema.optional(),
    /** Issue list (empty if Ready). */
    issues: z.array(IssueSchema),
    /** Optional human grade of the AI output. */
    aiGrade: z.enum(['A', 'B', 'F']).optional(),
    /** Optional human feedback for the AI output. */
    aiFeedback: z.string().optional(),
});
/** Tenant-aware external links for the audit detail view. */
export const AuditExternalLinksSchema = z.object({
    /** Direct link to the matching ServiceTitan job. */
    serviceTitanJobUrl: z.string().url(),
});
/** A full audit shape used by the audit viewer. */
export const AuditDetailSchema = AuditSummarySchema.extend({
    /** Tenant-aware links built by the API. */
    externalLinks: AuditExternalLinksSchema,
    /** Short scalar job metadata rendered in the generic details table. */
    formContent: z.record(z.string(), z.unknown()),
    /** Readable long-form job summary content. */
    jobSummary: z.string().optional(),
    /** Structured submitted form data for the dedicated form viewer. */
    submittedForms: z.array(SubmittedFormSchema),
    /** Structured estimate data for the dedicated estimate cards. */
    estimates: z.array(AuditEstimateSchema),
    /** Base audit output rendered as markdown/text. */
    auditOutputBase: z.string(),
    /** Optional secondary audit output (usually Markdown). */
    auditOutputSecondary: z.string().optional(),
});
/** Response shape for the audit list endpoint. */
export const AuditListResponseSchema = z.object({
    /** List results (already org-filtered). */
    items: z.array(AuditSummarySchema),
    /** Total number of matching audits before the server-side limit is applied. */
    total: z.number().int().nonnegative(),
});
/** A small “who am I” payload used to protect web routes. */
export const MeSchema = z.object({
    /** Current authenticated user (from session). */
    user: UserSchema,
    /** Active org for the request. */
    org: ActiveOrgSchema,
    /** Role of the user within the org. */
    role: OrgRoleSchema,
});
/** Query params supported by the audit list endpoint. */
export const AuditListQuerySchema = z.object({
    /** Optional free-text search across job id + technician + location. */
    search: z.string().optional(),
    /** Optional filter for inbox-like views. */
    filter: z.enum(['all', 'flagged']).optional(),
    /** Optional resolved-state filter backed by the app DB. */
    resolved: AuditResolvedFilterSchema.optional(),
    /** Optional requested_at date bucket (aligned with analytics ranges). */
    range: AnalyticsRangeSchema.optional(),
    /** Optional limit (server-enforced max). */
    limit: z.coerce.number().int().positive().max(200).optional(),
});
/** Payload for upserting an audit's local manual-review state. */
export const UpsertAuditReviewStateBodySchema = z.object({
    action: z.union([ReviewActionSchema, z.literal('feedback')]),
    sourceVersion: z.string().min(1),
    expectedUpdatedAt: z.string().datetime({ offset: true }).nullable(),
    expectedRevision: z.string().uuid().nullable().default(null),
    note: z.string().trim().max(4000).optional(),
    answers: AuditReviewAnswersSchema.optional(),
    explanations: AuditReviewExplanationsSchema.optional(),
}).strict().superRefine((value, ctx) => {
    if (value.action === 'feedback' ? !value.answers || !value.explanations : !value.note?.trim())
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Provide feedback answers or a reason for this review decision.' });
});
/** Payload for updating an audit's status. */
export const UpdateAuditStatusBodySchema = z.object({
    /** New audit status. */
    status: AuditStatusSchema,
});
/** Payload for grading AI output. */
export const GradeAiBodySchema = z.object({
    /** Grade selected by a human reviewer. */
    grade: z.enum(['A', 'B', 'F']),
    /** Optional free-form feedback. */
    feedback: z.string().max(5000).optional(),
});
/** Simple analytics summary used by the dashboard. */
export const AnalyticsSummarySchema = z.object({
    /** Total audits matching the time range. */
    totalAudits: z.number().int().nonnegative(),
    /** Count of audits that are not Ready. */
    flaggedCount: z.number().int().nonnegative(),
    /** Flagged percentage rounded to an integer. */
    flaggedPercent: z.number().int().min(0).max(100),
    /** Top issue types driving manual review. */
    topIssues: z.array(z.object({
        /** Issue type label. */
        type: z.string(),
        /** Number of occurrences. */
        count: z.number().int().nonnegative(),
    })),
});
/** Aggregate stats for admin review activity derived from audit_review_states. */
export const AuditReviewActivityStatsSchema = z.object({
    /** Total current review rows last updated in the selected range. */
    totalReviewedJobs: z.number().int().nonnegative(),
    /** Rows currently marked resolved. */
    resolvedCount: z.number().int().nonnegative(),
    /** Rows not currently marked resolved. */
    unresolvedCount: z.number().int().nonnegative(),
    /** Rows where the review says the audit caught a jobsite-alert-risk issue. */
    caughtJobsiteAlertIssueYesCount: z.number().int().nonnegative(),
    /** Rows where the review says the audit did not catch such an issue. */
    caughtJobsiteAlertIssueNoCount: z.number().int().nonnegative(),
    /** Rows where that question is still unanswered. */
    caughtJobsiteAlertIssueUnansweredCount: z.number().int().nonnegative(),
});
/** Per-user review activity row. */
export const AuditReviewActivityByUserSchema = z.object({
    /** Reviewer email when available; falls back to the updater id string if needed. */
    userEmail: z.string().min(1),
    /** Count of current review rows last saved by that user in-range. */
    reviewedCount: z.number().int().nonnegative(),
});
/** Detailed review activity row for the admin table. */
export const AuditReviewActivityRowSchema = z.object({
    /** Reviewer email when available; falls back to the updater id string if needed. */
    userEmail: z.string().min(1),
    /** External audit/job id. */
    jobId: z.string().min(1),
    /** Saved answer for whether the audit caught a jobsite-alert-risk issue. */
    caughtJobsiteAlertIssue: z.boolean().nullable(),
    /** Saved answer for whether the audit surfaced a potential installation issue. */
    awareOfPotentialInstallationIssue: z.boolean().nullable(),
    /** Saved answer for whether corrective action was taken on the job. */
    tookCorrectiveAction: z.boolean().nullable(),
});
/** Admin-only audit review activity payload. */
export const AuditReviewActivityResponseSchema = z.object({
    /** Aggregate stats for the selected range. */
    stats: AuditReviewActivityStatsSchema,
    /** Breakdown of in-range rows by the user who last updated them. */
    byUser: z.array(AuditReviewActivityByUserSchema),
    /** Current review rows last updated in-range. */
    rows: z.array(AuditReviewActivityRowSchema),
});
